// Personal accent color utilities.
//
// The chat feed (hype cards, big-sale reveal, duels, drumroll pill) colors itself
// based on the poster's personal accent. Users pick a color in My Profile;
// if none set, we fall back to electric blue.

export const DEFAULT_ACCENT = "#178AE5";

// A handful of hand-picked presets to seed the picker. Users can also enter any hex.
export const ACCENT_PRESETS = [
  { name: "Electric Blue", hex: "#178AE5" },
  { name: "Crimson",       hex: "#E13C32" },
  { name: "Jade",          hex: "#34C759" },
  { name: "Royal Violet",  hex: "#754ECA" },
  { name: "Sunset",        hex: "#E8921F" },
  { name: "Gold",          hex: "#FFC832" },
  { name: "Ice",           hex: "#41A0E8" },
  { name: "Rose",          hex: "#DA3756" },
  { name: "Emerald",       hex: "#1FAB7C" },
  { name: "Ember",         hex: "#E0511F" },
  { name: "Platinum",      hex: "#E5E7EB" },
  { name: "Aqua",          hex: "#1DC1A0" },
];

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex({ r, g, b }) {
  const toHex = (v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h *= 60;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (0 <= hp && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (1 <= hp && hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (2 <= hp && hp < 3) [r1, g1, b1] = [0, c, x];
  else if (3 <= hp && hp < 4) [r1, g1, b1] = [0, x, c];
  else if (4 <= hp && hp < 5) [r1, g1, b1] = [x, 0, c];
  else if (5 <= hp && hp < 6) [r1, g1, b1] = [c, 0, x];
  const m = l - c / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/** Auto-brighten a color that's too dark for a dark UI. Leaves already-vibrant colors alone. */
export function brighten(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  // Bump saturation for muddy colors, raise lightness for anything under ~48%.
  const bumped = {
    h: hsl.h,
    s: Math.max(hsl.s, 0.55),
    l: hsl.l < 0.48 ? Math.max(hsl.l + (0.48 - hsl.l), 0.5) : hsl.l,
  };
  return rgbToHex(hslToRgb(bumped));
}

/** Return an rgba() string from a hex + alpha 0..1. Safe on invalid input. */
export function rgba(hex, alpha = 1) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(23,138,229,${alpha})`;
  return `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${alpha})`;
}

/** Resolve the accent for a chat message.
 *  Preference order:
 *   1. `msg.metadata.accent_color` (snapshotted at post time, most stable)
 *   2. `usersById[msg.metadata.user_id].accent_color` (live lookup for legacy posts)
 *   3. DEFAULT_ACCENT
 *  Then auto-brightens for dark-UI readability.
 */
export function resolveAccent(msg, usersById) {
  const snapshot = msg?.metadata?.accent_color;
  const uid = msg?.metadata?.user_id || msg?.user_id;
  const live = uid && usersById ? usersById[uid]?.accent_color : null;
  const raw = snapshot || live || autoAccentFor(uid);
  return brighten(raw);
}

// Deterministic per-user fallback: reps who never picked a color still get their
// own consistent color (hashed from their user id) instead of everyone being blue.
export function autoAccentFor(uid) {
  if (!uid) return DEFAULT_ACCENT;
  let h = 0;
  const s = String(uid);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ACCENT_PRESETS[h % ACCENT_PRESETS.length].hex;
}
